import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useServiceRecordStatistics } from '@/hooks/useServiceRecords'
import { cn } from '@/lib/utils'

interface EmployeeDrilldownDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  departmentId: number
  departmentName: string
  dateRange?: { start_date?: string; end_date?: string }
}

/** 部门行下钻：复用按员工统计接口（scope=department）展示该一级部门内员工×类型明细。 */
export function EmployeeDrilldownDrawer({ open, onOpenChange, departmentId, departmentName, dateRange }: EmployeeDrilldownDrawerProps) {
  const { data, isLoading, isError } = useServiceRecordStatistics({
    start_date: dateRange?.start_date,
    end_date: dateRange?.end_date,
    scope: 'department',
    department_id: departmentId,
  }, open && departmentId > 0)

  const columns = data?.columns ?? []
  const rows = data?.rows ?? []

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:w-[70vw] sm:max-w-none overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{departmentName} · 员工明细</SheetTitle>
        </SheetHeader>
        <div className="mt-4">
          {isLoading ? (
            <div className="space-y-2">
              {[0, 1, 2].map(i => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : isError ? (
            <p className="py-8 text-center text-sm text-muted-foreground">加载失败，可能没有该部门的数据权限</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>员工</TableHead>
                    {columns.map(col => (
                      <TableHead key={col.type_id} className="whitespace-nowrap text-center">{col.label}</TableHead>
                    ))}
                    <TableHead className="whitespace-nowrap text-center">服务客户</TableHead>
                    <TableHead className="whitespace-nowrap text-center">服务次数</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(row => (
                    <TableRow key={row.user_id}>
                      <TableCell className="font-medium">{row.user_name || row.user_id}</TableCell>
                      {columns.map(col => {
                        const count = row.counts[String(col.type_id)] ?? 0
                        return (
                          <TableCell key={col.type_id} className={cn('text-center tabular-nums', count === 0 && 'text-muted-foreground/60')}>
                            {count}
                          </TableCell>
                        )
                      })}
                      <TableCell className="text-center tabular-nums">{row.customers}</TableCell>
                      <TableCell className="text-center font-semibold tabular-nums">{row.total}</TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={columns.length + 3} className="py-10 text-center text-sm text-muted-foreground">
                        该部门在所选范围内暂无服务记录
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
